import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Plus, Trash2, Pencil, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SlugRedirect {
  id: string;
  fromPath: string;
  toPath: string;
  type: '301' | '302';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RedirectForm {
  fromPath: string;
  toPath: string;
  type: '301' | '302';
  isActive: boolean;
}

const EMPTY_FORM: RedirectForm = {
  fromPath: '',
  toPath: '',
  type: '302',
  isActive: true,
};

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsRedirectsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/redirects`;

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<SlugRedirect | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SlugRedirect | null>(null);
  const [form, setForm] = useState<RedirectForm>(EMPTY_FORM);

  const { data = [], isLoading } = useQuery<SlugRedirect[]>({
    queryKey: ['cms-redirects', spaceId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId,
  });

  const createMutation = useMutation({
    mutationFn: (body: RedirectForm) => api.post(BASE, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-redirects', spaceId] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      toast.success('Redirect created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create redirect')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<RedirectForm> }) =>
      api.patch(`${BASE}/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-redirects', spaceId] });
      setEditTarget(null);
      setShowForm(false);
      setForm(EMPTY_FORM);
      toast.success('Redirect updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update redirect')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-redirects', spaceId] });
      setDeleteTarget(null);
      toast.success('Redirect deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`${BASE}/${id}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-redirects', spaceId] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(redirect: SlugRedirect) {
    setEditTarget(redirect);
    setForm({
      fromPath: redirect.fromPath,
      toPath: redirect.toPath,
      type: redirect.type,
      isActive: redirect.isActive,
    });
    setShowForm(true);
  }

  function handleSubmit() {
    if (!form.fromPath.trim() || !form.toPath.trim()) return;
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, body: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const submitError = editTarget ? updateMutation.error : createMutation.error;

  return (
    <div>
      <PageHeader
        title="URL Redirects"
        description="Manage slug redirects served by the customer portal. 301 = permanent, 302 = temporary."
        action={
          <Button onClick={openCreate} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Redirect
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={5} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<ArrowLeftRight size={26} />}
          title="No redirects yet"
          description="Create redirect rules to forward old URLs to new ones in the customer portal"
          action={{ label: 'New Redirect', onClick: openCreate }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  From
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50 transition-colors group">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.fromPath}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{r.toPath}</td>
                  <td className="px-4 py-3">
                    <Badge variant={r.type === '301' ? 'warning' : 'default'}>{r.type}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate({ id: r.id, isActive: !r.isActive })}
                      disabled={toggleMutation.isPending}
                      className="flex items-center gap-1.5 text-xs transition-colors"
                      title={r.isActive ? 'Click to disable' : 'Click to enable'}
                    >
                      {r.isActive ? (
                        <>
                          <ToggleRight size={16} className="text-emerald-500" />
                          <span className="text-emerald-600">Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft size={16} className="text-zinc-400" />
                          <span className="text-zinc-400">Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmt(r.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(r)}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        title="Edit"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditTarget(null);
          setForm(EMPTY_FORM);
        }}
        title={editTarget ? 'Edit Redirect' : 'New Redirect'}
      >
        <div className="space-y-4">
          <Input
            label="From path *"
            value={form.fromPath}
            onChange={(e) => setForm((f) => ({ ...f, fromPath: e.target.value }))}
            placeholder="/old-page"
            className="font-mono text-xs"
          />
          <Input
            label="To path *"
            value={form.toPath}
            onChange={(e) => setForm((f) => ({ ...f, toPath: e.target.value }))}
            placeholder="/new-page or https://…"
            className="font-mono text-xs"
          />
          <Select
            label="Redirect type"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as '301' | '302' }))}
          >
            <option value="302">302 — Temporary</option>
            <option value="301">301 — Permanent</option>
          </Select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
            />
            <span className="text-sm text-zinc-700">Active</span>
          </label>
        </div>
        {submitError && <p className="text-xs text-red-500 mt-3">{extractError(submitError)}</p>}
        <ModalFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowForm(false);
              setEditTarget(null);
              setForm(EMPTY_FORM);
            }}
          >
            Cancel
          </Button>
          <Button
            loading={isPending}
            disabled={!form.fromPath.trim() || !form.toPath.trim()}
            onClick={handleSubmit}
          >
            {editTarget ? 'Save changes' : 'Create Redirect'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete redirect?"
        message={
          deleteTarget
            ? `The redirect from "${deleteTarget.fromPath}" will be permanently deleted.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
